import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { AnnotatorDeposit, Item, Material, ItemType, DepositStatus } from 'types/api.types';
import { formatDateTime } from 'utils/dateFormat.utils';
import { reviewDeposit } from 'services/annotatorService';
import { PointsIcon } from 'components/common/PointsIcon';

const PRODUCT_SIZE_OPTIONS = [
  '7.5 oz',
  '12 oz',
  '16.9 oz',
  '32 oz',
  '2 L',
  '200 ml',
  '750 ml',
  'Other',
];

function resolveCatalogMaterialId(
  materialId: number | undefined,
  materials: Material[],
): number | undefined {
  if (!materialId) {
    return undefined;
  }
  const material = materials.find((entry) => entry.id === materialId);
  return material?.parentMaterialId ?? materialId;
}

function itemsForCatalogMaterial(
  items: Item[],
  catalogMaterialId: number | undefined,
  selectedItemId: number | undefined,
): Item[] {
  const filtered = catalogMaterialId
    ? items.filter((entry) => entry.material === catalogMaterialId)
    : items.filter((entry) => entry.type === ItemType.Reuse);

  if (!selectedItemId) {
    return filtered;
  }

  if (filtered.some((entry) => entry.id === selectedItemId)) {
    return filtered;
  }

  const selected = items.find((entry) => entry.id === selectedItemId);
  return selected ? [...filtered, selected] : filtered;
}

interface ReviewDialogProps {
  open: boolean;
  onClose: () => void;
  deposit: AnnotatorDeposit | null;
  items: Item[];
  materials: Material[];
  onReviewSubmit: (depositId: number) => void;
}

const ReviewDialog: React.FC<ReviewDialogProps> = ({
  open,
  onClose,
  deposit,
  items,
  materials,
  onReviewSubmit,
}) => {
  const [reviewStatus, setReviewStatus] = useState<DepositStatus>(DepositStatus.PendingReview);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<number | undefined>(deposit?.material);
  const [selectedItem, setSelectedItem] = useState<number | undefined>(deposit?.item);
  const [adminNotes, setAdminNotes] = useState<string>(deposit?.adminNotes || '');
  const [dirty, setDirty] = useState<boolean>(deposit?.dirty === 1);
  const [fraud, setFraud] = useState<boolean>(deposit?.fraud === 1);
  const [noObject, setNoObject] = useState<boolean>(deposit?.noObject === 1);
  const [brand, setBrand] = useState<string>(deposit?.brand || '');
  const [product, setProduct] = useState<string>(deposit?.product || '');
  const [productSize, setProductSize] = useState<string>(deposit?.product_size || '');
  const [productFlavor, setProductFlavor] = useState<string>(deposit?.product_flavor || '');
  const [multiObject, setMultiObject] = useState<boolean>(deposit?.multiObject === 1);

  useEffect(() => {
    if (!open || !deposit) {
      return;
    }

    setSelectedMaterial(deposit.material || undefined);
    setSelectedItem(deposit.item || undefined);
    setReviewStatus(DepositStatus.PendingReview);
    setAdminNotes(deposit.adminNotes || '');
    setDirty(deposit.dirty === 1);
    setFraud(deposit.fraud === 1);
    setNoObject(deposit.noObject === 1);
    setBrand(deposit.brand || '');
    setProduct(deposit.product || '');
    setProductSize(
      deposit.product_size && PRODUCT_SIZE_OPTIONS.includes(deposit.product_size)
        ? deposit.product_size
        : '',
    );
    setProductFlavor(deposit.product_flavor || '');
    setMultiObject(deposit.multiObject === 1);
  }, [open, deposit]);

  const catalogMaterialId = resolveCatalogMaterialId(selectedMaterial, materials);
  const selectableItems = itemsForCatalogMaterial(items, catalogMaterialId, selectedItem);

  const handleMaterialChange = (material: number) => {
    setSelectedMaterial(material);
    const nextCatalogMaterialId = resolveCatalogMaterialId(material, materials);
    const currentItem = items.find((entry) => entry.id === selectedItem);
    if (
      currentItem &&
      nextCatalogMaterialId &&
      currentItem.material !== nextCatalogMaterialId
    ) {
      setSelectedItem(undefined);
    }
  };

  if (!deposit) {
    return null;
  }

  const handleSubmitReview = async () => {
    if (!deposit?.id) return;
    setIsLoading(true);
    try {
      await reviewDeposit(deposit.id, {
        status: reviewStatus,
        material: selectedMaterial,
        item: selectedItem,
        adminNotes: adminNotes,
        dirty: dirty ? 1 : 0,
        fraud: fraud ? 1 : 0,
        noObject: noObject ? 1 : 0,
        brand: brand,
        product: product,
        product_size: productSize,
        product_flavor: productFlavor,
        multiObject: multiObject ? 1 : 0,
      });
      onReviewSubmit(deposit.id);
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitEnabled = () => {
    if (isLoading) {
      return false;
    }

    if (!reviewStatus || reviewStatus === DepositStatus.PendingReview) {
      return false;
    }

    if (!selectedItem) {
      return false;
    }

    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Review Deposit</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body1">
            <b>User ID:</b> {deposit.userId}
          </Typography>
          <Typography variant="body1">
            <b>Deposit ID:</b> {deposit.id}
          </Typography>
          <Typography variant="body1">
            <b>Deposit Date:</b> {formatDateTime(new Date(deposit.date))}
          </Typography>
          <Typography variant="body1">
            <b>Deposit Type:</b> {selectedMaterial ? 'Recycle' : 'Reuse'}
          </Typography>
          {deposit.itemName === 'Other' && deposit.product && (
            <Typography variant="body2" color="text.secondary">
              <b>Detected product:</b> {deposit.product}
            </Typography>
          )}
          {selectedMaterial && (
            <FormControl fullWidth sx={{ mt: 0 }}>
              <InputLabel id="material-select-label">Material</InputLabel>
              <Select
                labelId="material-select-label"
                id="material-select"
                value={selectedMaterial}
                label="Material"
                onChange={(e) => handleMaterialChange(e.target.value as number)}
              >
                {materials.map((material) => (
                  <MenuItem key={material.id} value={material.id}>
                    {material.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mt: 0 }}>
            <InputLabel id="item-select-label">Item</InputLabel>
            <Select
              labelId="item-select-label"
              id="item-select"
              value={selectedItem}
              label="Item"
              onChange={(e) => setSelectedItem(e.target.value as number | undefined)}
            >
              {selectableItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
            <PointsIcon />
            <Typography sx={{ fontWeight: 600 }}>{deposit?.points || 0}</Typography>
          </Box>
          <TextField
            label="Brand"
            fullWidth
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            sx={{ mt: 0 }}
          />
          <TextField
            label="Product"
            fullWidth
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            sx={{ mt: 0 }}
          />
          <TextField
            label="Product Flavor"
            fullWidth
            value={productFlavor}
            onChange={(e) => setProductFlavor(e.target.value)}
            sx={{ mt: 0 }}
          />
          <FormControl fullWidth sx={{ mt: 0 }}>
            <InputLabel id="product-size-select-label">Product Size</InputLabel>
            <Select
              labelId="product-size-select-label"
              value={productSize}
              label="Product Size"
              onChange={(e) => setProductSize(e.target.value as string)}
            >
              <MenuItem value="">None</MenuItem>
              {PRODUCT_SIZE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <img
              src={deposit.imageUrl}
              alt="Deposit Image"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={dirty} onChange={(e) => setDirty(e.target.checked)} />}
              label="Dirty"
            />
            <FormControlLabel
              control={<Checkbox checked={fraud} onChange={(e) => setFraud(e.target.checked)} />}
              label="Fraud"
            />
            <FormControlLabel
              control={
                <Checkbox checked={noObject} onChange={(e) => setNoObject(e.target.checked)} />
              }
              label="No Object"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={multiObject}
                  onChange={(e) => setMultiObject(e.target.checked)}
                />
              }
              label="Multi-Object"
            />
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="review-status-label">Review Status</InputLabel>
            <Select
              labelId="review-status-label"
              value={reviewStatus}
              label="Review Status"
              onChange={(e) => setReviewStatus(e.target.value as DepositStatus)}
            >
              {Object.values(DepositStatus)
                .filter((v) => !isNaN(Number(v)))
                .map((status) => (
                  <MenuItem key={status} value={status}>
                    {DepositStatus[status as number]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            multiline
            rows={4}
            fullWidth
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitReview}
          disabled={!isSubmitEnabled()}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          Submit Review
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewDialog;
